import { getApiErrorMessage } from '@/lib/api/error';
import { formatPhoneNumber, isPhoneNumberComplete } from '@/lib/phoneNumber';
import { AuthService } from '@/services/auth.service';
import type { RequestCodeMethod, ValidateCodeResponseDto } from '@/types/dto/phone-auth.dto';
import { useState } from 'react';
import { toast } from 'sonner';

interface UsePhoneVerificationParams {
  phoneNumber: string;
  verificationCode: string;
}

export function usePhoneVerification({ phoneNumber, verificationCode }: UsePhoneVerificationParams) {
  const [isRequestingCode, setIsRequestingCode] = useState(false);
  const [isValidatingCode, setIsValidatingCode] = useState(false);
  const [requestedMethod, setRequestedMethod] = useState<RequestCodeMethod | null>(null);

  const requestCode = async (method: RequestCodeMethod) => {
    const normalizedPhoneNumber = formatPhoneNumber(phoneNumber);

    if (!isPhoneNumberComplete(normalizedPhoneNumber) || isRequestingCode) {
      return false;
    }

    setRequestedMethod(method);
    setIsRequestingCode(true);

    try {
      await AuthService.requestCode({
        method,
        phoneNumber: normalizedPhoneNumber,
      });

      return true;
    } catch (error) {
      toast.error('Não foi possível enviar o código', {
        description: getApiErrorMessage(error, 'Confira o número informado e tente novamente.'),
      });

      return false;
    } finally {
      setIsRequestingCode(false);
    }
  };

  const validateCode = async (): Promise<ValidateCodeResponseDto | null> => {
    const normalizedPhoneNumber = formatPhoneNumber(phoneNumber);
    const normalizedCode = verificationCode.replace(/\D/g, '');

    if (!isPhoneNumberComplete(normalizedPhoneNumber) || normalizedCode.length < 6 || isValidatingCode) {
      return null;
    }

    setIsValidatingCode(true);

    try {
      return await AuthService.validateCode({
        code: normalizedCode,
        phoneNumber: normalizedPhoneNumber,
      });
    } catch (error) {
      toast.error('Não foi possível validar o código', {
        description: getApiErrorMessage(error, 'Confira o código recebido e tente novamente.'),
      });

      return null;
    } finally {
      setIsValidatingCode(false);
    }
  };

  return {
    isRequestingCode,
    isValidatingCode,
    requestCode,
    requestedMethod,
    validateCode,
  };
}
